import { Trans } from 'react-i18next'
import { CompleteArrow } from 'src/assets'
import { lowerLanguage } from 'src/i18n'
import { ContractorType, DataEntryStepProps, typedMemo } from 'src/types'
import { ResumePurchase } from 'src/ui/fragments'
import { Accordion, InputCheckbox, Recaptcha } from 'src/ui/widgets'

import { InsuredSummary } from './components/insured-summary/insured-summary'
import { LegalContractorSummary } from './components/legal-contractor-summary/legal-contractor-summary'
import { NaturalContractorSummary } from './components/natural-contractor-summary/natural-contractor-summary'
import { useDataEntryStep4 } from './data-entry-step-4.hook'

export const DataEntryStep4 = typedMemo((props: DataEntryStepProps) => {
  const {
    validationSchema,
    formik: { values, errors, touched, handleChange, handleBlur },
  } = props

  const {
    isDesktop,
    continueDisabled,
    col1Ref,
    col1Height,
    insuredListExpanded,
    documents,
    t,
    setInsuredListExpanded,
    openDocument,
  } = useDataEntryStep4(props)

  const mupDoc = documents?.find((doc) => doc.docCode === '2')
  const informativeDoc = documents?.find((doc) => doc.docCode === '1')

  const Contractor = (
    <div className="data-entry-step-container">
      <div className="contractor-tag mb-5">{t('data_entry_step_4.contractor_title')}</div>
      {values.contractorType === ContractorType.LEGAL ? (
        <LegalContractorSummary insurance={values} />
      ) : (
        <NaturalContractorSummary insurance={values} />
      )}
    </div>
  )

  const InsuredList = (
    <Accordion
      classesIcon="filter-00686F"
      classes="d-flex flex-column"
      icon={CompleteArrow}
      onToggle={setInsuredListExpanded}
      classesHead="data-entry-step-container d-flex flex-row justify-content-between align-items-center"
      head={
        <div>
          <div className="c-secondary fw-bold font-medium-m">
            {t('data_entry_step_4.other_insured_list_title')}
          </div>
          <div className="font-14-light ">{t('data_entry_step_4.other_insured_list_subtitle')}</div>
        </div>
      }
    >
      {values.insuredList.map((_, index) => (
        <div key={index} className="data-entry-step-container border-0">
          <InsuredSummary index={index} otherInsured={values.insuredList[index]} />
        </div>
      ))}
    </Accordion>
  )

  const Conditions = (
    <div>
      <div className="col-md-12 d-flex flex-column">
        <div className="mb-3">
          <InputCheckbox
            name="consentTd"
            handleChange={handleChange}
            handleBlur={handleBlur}
            validationSchema={validationSchema}
            touched={touched.consentTd}
            value={values.consentTd}
            error={errors.consentTd && touched.consentTd ? errors.consentTd : ''}
            label={t('data_entry_step_4.consent_accordion_value')}
            classesLabel="m-0 font-16 d-flex align-items-baseline"
          />
        </div>
        <div>
          <InputCheckbox
            name="informativeVision"
            handleChange={handleChange}
            handleBlur={handleBlur}
            validationSchema={validationSchema}
            touched={touched.informativeVision}
            value={values.informativeVision}
            error={
              errors.informativeVision && touched.informativeVision ? errors.informativeVision : ''
            }
            LabelComponent={
              <Trans
                i18nKey="data_entry_step_4.informative_vision"
                components={{
                  mup: (
                    <a
                      className="link-reset c-2727ab"
                      onClick={() => openDocument(mupDoc?.filePath[lowerLanguage])}
                    />
                  ),
                  informative: (
                    <a
                      className="link-reset c-2727ab"
                      onClick={() => openDocument(informativeDoc?.filePath[lowerLanguage])}
                    />
                  ),
                }}
              />
            }
            classesLabel="m-0 font-16 d-flex align-items-baseline"
          />
        </div>
      </div>
    </div>
  )

  return (
    <>
      <Recaptcha />
      <div className="d-flex gap-4">
        <div ref={col1Ref} className="col-sm-12 col-xl-8">
          {Contractor}
          {values.insuredList.length > 0 && InsuredList}
          <div className="mb-4 border-top" />
          <div>{Conditions}</div>
        </div>
        <div
          className={`d-xl-block d-none col ${insuredListExpanded && 'resume-sticky'}`}
          style={{
            height: col1Height ? `${col1Height}px` : 'auto',
          }}
        >
          {isDesktop && (
            <ResumePurchase formik={props.formik} continueDisabled={continueDisabled} />
          )}
        </div>
      </div>
    </>
  )
})
